import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Car, LogOut, Plus, Search, BarChart3, Calculator, 
  Gauge, Package, DollarSign, TrendingUp, Sparkles, Trash2, Edit, Menu, X, ArrowUpRight, ArrowDownRight, Settings, Users, Store, Image, PieChart, FileText, Download, Scan, Network, Receipt, Wrench, CreditCard, FileSpreadsheet, Camera
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { StatsSkeleton, ChartSkeleton, VehicleCardSkeleton } from '../components/ui/skeletons';
import NotificationBell from '../components/NotificationBell';
import NotificationSettings from '../components/NotificationSettings';
import DealerCRM from '../components/DealerCRM';
import SocialListingGenerator from '../components/SocialListingGenerator';
import WholesaleMarketplace from '../components/WholesaleMarketplace';
import AdvancedAnalytics from '../components/AdvancedAnalytics';
import ImageUploader from '../components/ImageUploader';
import VehicleHistory from '../components/VehicleHistory';
import LocationManager from '../components/LocationManager';
import DocumentManager from '../components/DocumentManager';
import BulkVINScanner from '../components/BulkVINScanner';
import LeadNetwork from '../components/LeadNetwork';
import InvoiceManager from '../components/InvoiceManager';
import ExpenseTracker from '../components/ExpenseTracker';
import RepairLog from '../components/RepairLog';
import InventorySync from '../components/InventorySync';
import DealerImagePro from '../components/DealerImagePro';
import DocumentCenter from '../components/DocumentCenter';
import ProfileSettings from '../components/ProfileSettings';
import IdentityVerification from '../components/IdentityVerification';
import SubscriptionExpiredOverlay from '../components/SubscriptionExpiredOverlay';
import LanguagePicker from '../components/LanguagePicker';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Area, AreaChart } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import api from '../utils/api';

const NAV_KEYS = [
  { id: 'overview', icon: BarChart3, tKey: 'nav.overview', color: 'from-blue-500 to-cyan-500' },
  { id: 'inventory', icon: Package, tKey: 'nav.inventory', color: 'from-purple-500 to-pink-500' },
  { id: 'crm', icon: Users, tKey: 'nav.crm', color: 'from-indigo-500 to-blue-500' },
  { id: 'wholesale', icon: Store, tKey: 'nav.wholesale', color: 'from-green-500 to-emerald-500' },
  { id: 'analytics', icon: PieChart, tKey: 'nav.analytics', color: 'from-cyan-500 to-blue-500' },
  { id: 'vin-decoder', icon: Gauge, tKey: 'nav.vinDecoder', color: 'from-orange-500 to-amber-500' },
  { id: 'profit-calc', icon: Calculator, tKey: 'nav.profitCalc', color: 'from-green-500 to-emerald-500' },
  { id: 'ai-tools', icon: Sparkles, tKey: 'nav.aiTools', color: 'from-violet-500 to-purple-500' },
  { id: 'lead-network', icon: Network, tKey: 'nav.leadNetwork', color: 'from-rose-500 to-orange-500' },
  { id: 'invoices', icon: Receipt, tKey: 'nav.salesInvoices', color: 'from-emerald-500 to-teal-500' },
  { id: 'expenses', icon: CreditCard, tKey: 'nav.expenses', color: 'from-red-500 to-rose-500' },
  { id: 'repairs', icon: Wrench, tKey: 'nav.repairs', color: 'from-orange-500 to-amber-500' },
  { id: 'documents', icon: FileText, tKey: 'nav.docCenter', color: 'from-amber-500 to-orange-500' },
  { id: 'image-pro', icon: Camera, tKey: 'nav.imagePro', color: 'from-cyan-500 to-blue-600' },
  { id: 'import-export', icon: FileSpreadsheet, tKey: 'nav.importExport', color: 'from-indigo-500 to-blue-500' },
  { id: 'settings', icon: Settings, tKey: 'nav.settings', color: 'from-gray-500 to-slate-500' },
];

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [subscriptionExpired, setSubscriptionExpired] = useState(false);
  
  // Data states
  const [vehicles, setVehicles] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [showBulkVIN, setShowBulkVIN] = useState(false);

  // Form states
  const [vehicleForm, setVehicleForm] = useState({
    vin: '', stock_number: '', make: '', model: '', year: new Date().getFullYear(),
    price: '', purchase_price: '', mileage: '', condition: 'good',
    color: '', title_status: 'clean', auction_source: '', lot_location: '',
    description: '', status: 'available', features: [], images: [], remarks: ''
  });
  
  const [vinInput, setVinInput] = useState('');
  const [vinResult, setVinResult] = useState(null);
  const [vinPricing, setVinPricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [vinAutoLoading, setVinAutoLoading] = useState(false);

  const handleVinAutoFill = async (vin) => {
    if (vin.length !== 17) return;
    setVinAutoLoading(true);
    try {
      const { data } = await api.get(`/api/vin/decode/${vin}`);
      if (data) {
        setVehicleForm(prev => ({
          ...prev,
          make: data.manufacturer || prev.make,
          model: data.model || prev.model,
          year: data.year || prev.year,
          description: data.body_type ? `${data.body_type} - ${data.engine || ''} ${data.transmission || ''}`.trim() : prev.description
        }));
        toast.success(`VIN decoded: ${data.year} ${data.manufacturer} ${data.model}`);
      }
    } catch {
      // Silent fail — user can fill manually
    }
    setVinAutoLoading(false);
  };
  
  const [profitCalc, setProfitCalc] = useState({
    purchase_price: '', asking_price: '', repair_costs: '',
    holding_costs: '', selling_costs: ''
  });
  const [profitResult, setProfitResult] = useState(null);
  
  const [aiForm, setAiForm] = useState({
    make: '', model: '', year: '', mileage: '', condition: 'good', features: []
  });
  const [aiDescription, setAiDescription] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [vehiclesRes, analyticsRes, salesRes] = await Promise.all([
        api.get('/api/vehicles'),
        api.get('/api/analytics/dashboard'),
        api.get('/api/analytics/sales?days=30')
      ]);
      setVehicles(vehiclesRes.data);
      setAnalytics(analyticsRes.data);
      setSalesData(salesRes.data.data || []);
      // Check subscription status
      try {
        const { data: subStatus } = await api.get('/api/subscriptions/status');
        setSubscriptionExpired(subStatus.expired === true);
      } catch { /* ignore */ }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Check for subscription success
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const success = searchParams.get('success');
    
    if (sessionId && success) {
      const pollPaymentStatus = async (attempts = 0) => {
        if (attempts >= 5) return;
        try {
          const { data } = await api.get(`/api/subscriptions/status/${sessionId}`);
          if (data.payment_status === 'paid') {
            toast.success('Subscription activated successfully!');
            navigate('/dashboard', { replace: true });
          } else {
            setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
          }
        } catch {
          setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
        }
      };
      pollPaymentStatus();
    }
  }, [searchParams, navigate]);

  // Handle subscribe redirect from pricing page
  useEffect(() => {
    const planId = searchParams.get('subscribe');
    const billing = searchParams.get('billing') || 'monthly';
    if (planId && user) {
      const startCheckout = async () => {
        try {
          const { data } = await api.post('/api/subscriptions/checkout', {
            plan_id: planId,
            billing_period: billing,
            origin_url: window.location.origin
          });
          if (data.url) {
            window.location.href = data.url;
          }
        } catch (err) {
          const detail = err.response?.data?.detail;
          if (typeof detail === 'string') toast.error(detail);
          else toast.error('Failed to start checkout');
          navigate('/dashboard', { replace: true });
        }
      };
      startCheckout();
    }
  }, [searchParams, user, navigate]);

  // Listen for subscription expired events from API interceptor
  useEffect(() => {
    const handler = () => setSubscriptionExpired(true);
    window.addEventListener('subscription-expired', handler);
    return () => window.removeEventListener('subscription-expired', handler);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleAddVehicle = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...vehicleForm,
        year: parseInt(vehicleForm.year),
        price: parseFloat(vehicleForm.price),
        purchase_price: vehicleForm.purchase_price ? parseFloat(vehicleForm.purchase_price) : null,
        mileage: vehicleForm.mileage ? parseInt(vehicleForm.mileage) : null,
        stock_number: vehicleForm.stock_number || null,
        color: vehicleForm.color || null,
        title_status: vehicleForm.title_status || null,
        auction_source: vehicleForm.auction_source || null,
        lot_location: vehicleForm.lot_location || null,
        remarks: vehicleForm.remarks || null
      };
      
      if (editingVehicle) {
        await api.put(`/api/vehicles/${editingVehicle.id}`, data);
        toast.success('Vehicle updated!');
      } else {
        await api.post('/api/vehicles', data);
        toast.success('Vehicle added!');
      }
      
      setShowAddVehicle(false);
      setEditingVehicle(null);
      setVehicleForm({
        vin: '', stock_number: '', make: '', model: '', year: new Date().getFullYear(),
        price: '', purchase_price: '', mileage: '', condition: 'good',
        color: '', title_status: 'clean', auction_source: '', lot_location: '',
        description: '', status: 'available', features: [], images: [], remarks: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save vehicle');
    }
  };

  const handleDeleteVehicle = async (id) => {
    if (!window.confirm('Are you sure you want to delete this vehicle?')) return;
    try {
      await api.delete(`/api/vehicles/${id}`);
      toast.success('Vehicle deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete vehicle');
    }
  };

  const handleDecodeVin = async () => {
    if (vinInput.length !== 17) {
      toast.error('VIN must be exactly 17 characters');
      return;
    }
    setVinPricing(null);
    try {
      const { data } = await api.get(`/api/vin/decode/${vinInput}`);
      setVinResult(data);
      // Auto-fetch pricing
      setPricingLoading(true);
      try {
        const { data: pricing } = await api.post('/api/vehicles/auto-price', {
          make: data.manufacturer, model: data.model, year: data.year,
          condition: 'good', body_type: data.body_type, engine: data.engine
        });
        setVinPricing(pricing);
      } catch {
        // Pricing is optional - silently ignore
      } finally {
        setPricingLoading(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decode VIN');
    }
  };

  const handleCalculateProfit = async () => {
    try {
      const { data } = await api.post('/api/tools/profit-calculator', {
        purchase_price: parseFloat(profitCalc.purchase_price) || 0,
        asking_price: parseFloat(profitCalc.asking_price) || 0,
        repair_costs: parseFloat(profitCalc.repair_costs) || 0,
        holding_costs: parseFloat(profitCalc.holding_costs) || 0,
        selling_costs: parseFloat(profitCalc.selling_costs) || 0
      });
      setProfitResult(data);
    } catch {
      toast.error('Failed to calculate profit');
    }
  };

  const handleGenerateDescription = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post('/api/ai/generate-description', {
        make: aiForm.make,
        model: aiForm.model,
        year: parseInt(aiForm.year),
        mileage: aiForm.mileage ? parseInt(aiForm.mileage) : null,
        condition: aiForm.condition,
        features: aiForm.features
      });
      setAiDescription(data.description);
    } catch {
      toast.error('Failed to generate description');
    } finally {
      setAiLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    try {
      const { data } = await api.post('/api/subscriptions/checkout', {
        plan_id: planId,
        origin_url: window.location.origin
      });
      window.location.href = data.url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 && typeof detail === 'string') {
        toast.error(detail, { duration: 6000 });
        if (detail.includes('upload your ID')) {
          setActiveTab('settings');
        }
      } else {
        toast.error('Failed to start checkout');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex">
        {/* Sidebar Skeleton */}
        <aside className="hidden lg:block w-72 bg-white border-r border-gray-200">
          <div className="p-6 border-b border-gray-100">
            <div className="h-12 w-32 bg-gray-200 rounded-lg animate-pulse" />
          </div>
          <nav className="p-4 space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-100 rounded-xl animate-pulse" />
            ))}
          </nav>
        </aside>
        
        {/* Main Content Skeleton */}
        <main className="flex-1 p-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="h-8 w-48 bg-gray-200 rounded-lg animate-pulse mb-2" />
              <div className="h-4 w-32 bg-gray-100 rounded animate-pulse" />
            </div>
            <div className="h-12 w-36 bg-gray-200 rounded-xl animate-pulse" />
          </div>
          <StatsSkeleton />
          <div className="grid lg:grid-cols-2 gap-6 mt-8">
            <ChartSkeleton />
            <ChartSkeleton />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Subscription Expired Overlay */}
      {subscriptionExpired && user?.role !== 'admin' && <SubscriptionExpiredOverlay />}
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-72 bg-white border-r border-gray-200 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} shadow-xl lg:shadow-none`}>
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b border-gray-100">
            <Link to="/" className="flex items-center gap-2" data-testid="dashboard-logo">
              <img 
                src="/wakzy-logo.png" 
                alt="Wakzy Auto Tech" 
                className="h-12 w-auto"
              />
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1.5 overflow-y-auto">
            {NAV_KEYS.map((item) => (
              <button
                key={item.id}
                onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 ${
                  activeTab === item.id 
                    ? `bg-gradient-to-r ${item.color} text-white shadow-lg` 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium text-sm">{t(item.tKey)}</span>
              </button>
            ))}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-gray-100">
            <div className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-gradient-to-r from-gray-50 to-gray-100">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                {user?.name?.[0] || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate">{user?.name}</p>
                <p className="text-sm text-gray-500 truncate">{user?.email}</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              className="w-full border-gray-200 text-gray-600 hover:bg-gray-100 rounded-xl"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" /> {t('nav.signOut')}
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-gray-200 px-4 py-3 shadow-sm">
        <div className="flex items-center justify-between">
          <button onClick={() => setSidebarOpen(true)} className="p-2 rounded-lg hover:bg-gray-100" data-testid="mobile-menu-btn">
            <Menu className="w-6 h-6 text-gray-600" />
          </button>
          <img src="/wakzy-logo.png" alt="Wakzy" className="h-8" />
          <LanguagePicker />
        </div>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main Content */}
      <main className="flex-1 lg:ml-72 pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 
                className="text-3xl font-bold text-gray-900 tracking-tight"
                style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
                data-testid="dashboard-title"
              >
                {NAV_KEYS.find(n => n.id === activeTab)?.tKey ? t(NAV_KEYS.find(n => n.id === activeTab).tKey) : activeTab}
              </h1>
              <p className="text-gray-500 mt-1">
                {user?.subscription_plan 
                  ? <span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-green-500"></span>{user.subscription_plan.charAt(0).toUpperCase() + user.subscription_plan.slice(1)} Plan</span>
                  : (() => {
                      const trialEnd = user?.trial_ends_at ? new Date(user.trial_ends_at) : null;
                      const daysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd - Date.now()) / 86400000)) : null;
                      return daysLeft !== null && daysLeft > 0
                        ? <span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-amber-500"></span>Free Trial — {daysLeft} day{daysLeft !== 1 ? 's' : ''} left</span>
                        : daysLeft === 0
                          ? <span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-500"></span>Trial Expired</span>
                          : <span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-amber-500"></span>Free Trial</span>;
                    })()
                }
              </p>
            </div>
            <div className="flex items-center gap-3">
              <LanguagePicker />
              <NotificationBell />
              {activeTab === 'inventory' && (
                <div className="flex items-center gap-2">
                  <div className="flex items-center rounded-xl border border-gray-200 overflow-hidden">
                    <button className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-1.5" onClick={() => window.open(`${API_URL}/api/export/inventory/csv`, '_blank')} data-testid="export-inventory-csv">
                      <Download className="w-3.5 h-3.5" /> CSV
                    </button>
                    <div className="w-px h-6 bg-gray-200" />
                    <button className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-1.5" onClick={() => window.open(`${API_URL}/api/export/inventory/pdf`, '_blank')} data-testid="export-inventory-pdf">
                      <FileText className="w-3.5 h-3.5" /> PDF
                    </button>
                  </div>
                  <Button 
                    variant="outline"
                    className="rounded-xl border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                    onClick={() => setShowBulkVIN(true)}
                    data-testid="bulk-vin-btn"
                  >
                    <Scan className="w-4 h-4 mr-2" /> Bulk Import
                  </Button>
                  <Button 
                    className="bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90 text-white rounded-xl shadow-lg shadow-purple-500/25"
                    onClick={() => setShowAddVehicle(true)}
                    data-testid="add-vehicle-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Add Vehicle
                  </Button>
                </div>
              )}
              {activeTab === 'crm' && (
                <div className="flex items-center rounded-xl border border-gray-200 overflow-hidden">
                  <button className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-1.5" onClick={() => window.open(`${API_URL}/api/export/crm/csv`, '_blank')} data-testid="export-crm-csv">
                    <Download className="w-3.5 h-3.5" /> CSV
                  </button>
                  <div className="w-px h-6 bg-gray-200" />
                  <button className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-1.5" onClick={() => window.open(`${API_URL}/api/export/crm/pdf`, '_blank')} data-testid="export-crm-pdf">
                    <FileText className="w-3.5 h-3.5" /> PDF
                  </button>
                </div>
              )}
              {activeTab === 'analytics' && (
                <div className="flex items-center rounded-xl border border-gray-200 overflow-hidden">
                  <button className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-1.5" onClick={() => window.open(`${API_URL}/api/export/analytics/csv`, '_blank')} data-testid="export-analytics-csv">
                    <Download className="w-3.5 h-3.5" /> CSV
                  </button>
                  <div className="w-px h-6 bg-gray-200" />
                  <button className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-1.5" onClick={() => window.open(`${API_URL}/api/export/analytics/pdf`, '_blank')} data-testid="export-analytics-pdf">
                    <FileText className="w-3.5 h-3.5" /> PDF
                  </button>
                </div>
              )}
              {activeTab === 'wholesale' && (
                <Button 
                  variant="outline"
                  className="rounded-xl border-gray-200 text-gray-600 hover:bg-gray-50"
                  onClick={() => window.open(`${API_URL}/api/export/wholesale/csv`, '_blank')}
                  data-testid="export-wholesale-btn"
                >
                  <Download className="w-4 h-4 mr-2" /> Export CSV
                </Button>
              )}
            </div>
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && analytics && (
            <div className="space-y-8">
              {/* Stats Grid */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { label: t('dashboard.totalVehicles'), value: analytics.total_vehicles, icon: Package, color: 'from-blue-500 to-cyan-500', change: '+12%', up: true },
                  { label: t('dashboard.available'), value: analytics.available, icon: Car, color: 'from-green-500 to-emerald-500', change: '+8%', up: true },
                  { label: t('dashboard.inventoryValue'), value: `$${(analytics.inventory_value || 0).toLocaleString()}`, icon: DollarSign, color: 'from-amber-500 to-orange-500', change: '+24%', up: true },
                  { label: t('dashboard.conversionRate'), value: `${analytics.conversion_rate}%`, icon: TrendingUp, color: 'from-purple-500 to-pink-500', change: '-2%', up: false },
                ].map((stat, idx) => (
                  <div key={idx} className="relative overflow-hidden bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300" data-testid={`stat-card-${idx}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${stat.color} flex items-center justify-center shadow-lg`}>
                        <stat.icon className="w-6 h-6 text-white" />
                      </div>
                      <div className={`flex items-center gap-1 text-sm font-medium ${stat.up ? 'text-green-600' : 'text-red-500'}`}>
                        {stat.up ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                        {stat.change}
                      </div>
                    </div>
                    <div className="text-3xl font-bold text-gray-900">{stat.value}</div>
                    <div className="text-sm text-gray-500 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* Charts */}
              <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
                      <TrendingUp className="w-4 h-4 text-white" />
                    </div>
                    Sales Trend (30 Days)
                  </h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={salesData}>
                        <defs>
                          <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#0052FF" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#0052FF" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#6B7280' }} />
                        <YAxis tick={{ fontSize: 12, fill: '#6B7280' }} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#fff', 
                            border: 'none', 
                            borderRadius: '12px', 
                            boxShadow: '0 10px 40px rgba(0,0,0,0.1)' 
                          }} 
                        />
                        <Area type="monotone" dataKey="count" stroke="#0052FF" strokeWidth={3} fill="url(#colorCount)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">
                      <DollarSign className="w-4 h-4 text-white" />
                    </div>
                    Revenue Trend
                  </h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={salesData}>
                        <defs>
                          <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10B981" stopOpacity={1}/>
                            <stop offset="95%" stopColor="#059669" stopOpacity={1}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#6B7280' }} />
                        <YAxis tick={{ fontSize: 12, fill: '#6B7280' }} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#fff', 
                            border: 'none', 
                            borderRadius: '12px', 
                            boxShadow: '0 10px 40px rgba(0,0,0,0.1)' 
                          }} 
                        />
                        <Bar dataKey="revenue" fill="url(#colorRevenue)" radius={[8, 8, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* Subscription CTA */}
              {!user?.subscription_plan && (
                <div className="relative overflow-hidden bg-gradient-to-r from-[#0052FF] via-[#7C3AED] to-[#00D4FF] rounded-2xl p-8 text-white shadow-2xl">
                  <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIzMCIgY3k9IjMwIiByPSIyIi8+PC9nPjwvZz48L3N2Zz4=')] opacity-30" />
                  <div className="relative z-10">
                    <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                      Unlock Premium Features
                    </h3>
                    <p className="mb-6 opacity-90">
                      {(() => {
                        const trialEnd = user?.trial_ends_at ? new Date(user.trial_ends_at) : null;
                        const daysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd - Date.now()) / 86400000)) : null;
                        return daysLeft !== null && daysLeft > 0
                          ? `Your free trial ends in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}. Subscribe now to keep all features.`
                          : daysLeft === 0
                            ? 'Your free trial has expired. Subscribe to continue using premium features.'
                            : 'Subscribe to access AI tools, unlimited listings, and advanced analytics.';
                      })()}
                    </p>
                    <div className="flex gap-4 flex-wrap">
                      <Button 
                        onClick={() => handleSubscribe('starter')}
                        className="bg-white text-[#0052FF] hover:bg-gray-100 rounded-xl shadow-lg"
                        data-testid="subscribe-starter-btn"
                      >
                        Starter - $22.99/mo
                      </Button>
                      <Button 
                        onClick={() => handleSubscribe('professional')}
                        className="bg-white text-[#0052FF] hover:bg-gray-100 rounded-xl shadow-lg"
                        data-testid="subscribe-professional-btn"
                      >
                        Professional - $59.99/mo
                      </Button>
                      <Button 
                        onClick={() => handleSubscribe('dealer_pro')}
                        className="bg-white text-[#0052FF] hover:bg-gray-100 rounded-xl shadow-lg"
                        data-testid="subscribe-dealer-pro-btn"
                      >
                        Dealer Pro - $99.99/mo
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Inventory Tab */}
          {activeTab === 'inventory' && (
            <div className="space-y-6">
              {/* Search */}
              <div className="flex gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input 
                    placeholder="Search vehicles..." 
                    className="pl-12 h-12 rounded-xl border-gray-200 focus:border-purple-500 focus:ring-purple-500"
                    data-testid="search-vehicles-input"
                  />
                </div>
              </div>

              {/* Vehicles Grid */}
              {vehicles.length === 0 ? (
                <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-gray-100">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <Package className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">No vehicles yet</h3>
                  <p className="text-gray-500 mb-6">Add your first vehicle to get started</p>
                  <Button 
                    onClick={() => setShowAddVehicle(true)}
                    className="bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90 text-white rounded-xl shadow-lg"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Add Vehicle
                  </Button>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {vehicles.map((vehicle) => (
                    <div key={vehicle.id} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 group" data-testid={`vehicle-card-${vehicle.id}`}>
                      <div className="h-48 bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center relative overflow-hidden">
                        {vehicle.images && vehicle.images.length > 0 ? (
                          <img src={vehicle.images[0]} alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        ) : (
                          <Car className="w-20 h-20 text-gray-300 group-hover:scale-110 transition-transform duration-300" />
                        )}
                        <div className="absolute top-3 right-3">
                          <span className={`text-xs font-semibold px-3 py-1.5 rounded-full backdrop-blur-sm ${
                            vehicle.status === 'available' ? 'bg-green-100/90 text-green-700' :
                            vehicle.status === 'pending' ? 'bg-amber-100/90 text-amber-700' :
                            'bg-gray-100/90 text-gray-700'
                          }`}>
                            {vehicle.status.charAt(0).toUpperCase() + vehicle.status.slice(1)}
                          </span>
                        </div>
                        {vehicle.images && vehicle.images.length > 1 && (
                          <div className="absolute bottom-2 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded-lg backdrop-blur-sm">
                            <Image className="w-3 h-3 inline mr-1" />{vehicle.images.length} photos
                          </div>
                        )}
                        {vehicle.stock_number && (
                          <div className="absolute top-3 left-3 bg-black/60 text-white text-xs font-mono px-2 py-1 rounded-lg backdrop-blur-sm">
                            #{vehicle.stock_number}
                          </div>
                        )}
                      </div>
                      <div className="p-5">
                        <div className="mb-3">
                          <h3 className="font-bold text-gray-900 text-lg leading-tight">
                            {vehicle.year} {vehicle.make} {vehicle.model}
                          </h3>
                          <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                            {vehicle.color && <span className="bg-gray-100 px-2 py-0.5 rounded">{vehicle.color}</span>}
                            {vehicle.mileage && <span>{vehicle.mileage.toLocaleString()} mi</span>}
                            {vehicle.title_status && vehicle.title_status !== 'clean' && <span className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded">{vehicle.title_status}</span>}
                          </div>
                          <p className="text-xs text-gray-400 mt-1 font-mono">{vehicle.vin || 'No VIN'}</p>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
                            ${vehicle.price?.toLocaleString()}
                          </span>
                          <div className="flex gap-2">
                            <button 
                              onClick={() => {
                                setEditingVehicle(vehicle);
                                setVehicleForm({
                                  vin: vehicle.vin || '',
                                  stock_number: vehicle.stock_number || '',
                                  make: vehicle.make,
                                  model: vehicle.model,
                                  year: vehicle.year,
                                  price: vehicle.price,
                                  purchase_price: vehicle.purchase_price || '',
                                  mileage: vehicle.mileage || '',
                                  condition: vehicle.condition,
                                  color: vehicle.color || '',
                                  title_status: vehicle.title_status || 'clean',
                                  auction_source: vehicle.auction_source || '',
                                  lot_location: vehicle.lot_location || '',
                                  description: vehicle.description || '',
                                  status: vehicle.status,
                                  features: vehicle.features || [],
                                  images: vehicle.images || [],
                                  remarks: vehicle.remarks || ''
                                });
                                setShowAddVehicle(true);
                              }}
                              className="p-2 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-all"
                              data-testid={`edit-vehicle-${vehicle.id}`}
                            >
                              <Edit className="w-5 h-5" />
                            </button>
                            <button 
                              onClick={() => handleDeleteVehicle(vehicle.id)}
                              className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all"
                              data-testid={`delete-vehicle-${vehicle.id}`}
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* VIN Decoder Tab */}
          {activeTab === 'vin-decoder' && (
            <div className="max-w-2xl">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 flex items-center justify-center shadow-lg">
                    <Gauge className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-xl">VIN Decoder</h3>
                    <p className="text-gray-500">Enter a 17-character VIN to decode</p>
                  </div>
                </div>
                
                <div className="flex gap-4 mb-8">
                  <Input 
                    placeholder="Enter 17-character VIN"
                    value={vinInput}
                    onChange={(e) => setVinInput(e.target.value.toUpperCase())}
                    maxLength={17}
                    className="flex-1 h-14 text-lg font-mono rounded-xl border-gray-200 focus:border-orange-500 focus:ring-orange-500"
                    data-testid="vin-decoder-input"
                  />
                  <Button 
                    onClick={handleDecodeVin}
                    className="bg-gradient-to-r from-orange-500 to-amber-500 hover:opacity-90 text-white px-8 rounded-xl shadow-lg"
                    data-testid="vin-decode-btn"
                  >
                    Decode
                  </Button>
                </div>

                {vinResult && (
                  <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-6 border border-orange-100" data-testid="vin-result">
                    <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                      Decoded Vehicle Information
                      <span className="ml-auto text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">NHTSA Verified</span>
                    </h4>
                    {/* Primary Info */}
                    <div className="bg-white rounded-xl p-5 mb-4 border border-orange-100">
                      <p className="text-2xl font-bold text-gray-900">{vinResult.year} {vinResult.manufacturer} {vinResult.model}</p>
                      {vinResult.trim && vinResult.trim !== 'N/A' && <p className="text-gray-500 mt-1">{vinResult.trim} Trim</p>}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {[
                        { label: 'Body Type', value: vinResult.body_type },
                        { label: 'Engine', value: vinResult.engine },
                        { label: 'Horsepower', value: vinResult.horsepower },
                        { label: 'Transmission', value: vinResult.transmission },
                        { label: 'Drive Type', value: vinResult.drive_type },
                        { label: 'Fuel Type', value: vinResult.fuel_type },
                        { label: 'Vehicle Type', value: vinResult.vehicle_type },
                        { label: 'Doors', value: vinResult.doors },
                        { label: 'Country', value: vinResult.country },
                        { label: 'Plant City', value: vinResult.plant_city },
                        { label: 'GVWR', value: vinResult.gvwr },
                        { label: 'VIN', value: vinResult.vin },
                      ].filter(f => f.value && f.value !== 'Unknown' && f.value !== 'N/A' && f.value !== '').map(field => (
                        <div key={field.label} className="bg-white rounded-xl p-3">
                          <span className="text-xs text-gray-500 uppercase tracking-wider">{field.label}</span>
                          <p className="font-semibold text-gray-900 mt-0.5 text-sm">{field.value}</p>
                        </div>
                      ))}
                    </div>
                    
                    {/* AI Market Pricing */}
                    {pricingLoading && (
                      <div className="mt-4 bg-white rounded-xl p-5 border border-orange-100 animate-pulse">
                        <div className="flex items-center gap-2 mb-3">
                          <DollarSign className="w-5 h-5 text-orange-400" />
                          <span className="text-sm font-medium text-gray-500">Estimating market value...</span>
                        </div>
                        <div className="h-8 bg-gray-100 rounded-lg w-48" />
                      </div>
                    )}
                    {vinPricing && !pricingLoading && (
                      <div className="mt-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-5 border border-emerald-200" data-testid="vin-pricing-result">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
                            <DollarSign className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-emerald-800">AI Market Price Estimate</p>
                            <p className="text-xs text-emerald-600">Based on vehicle specs & market data</p>
                          </div>
                          <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${
                            vinPricing.confidence === 'high' ? 'bg-emerald-100 text-emerald-700' :
                            vinPricing.confidence === 'medium' ? 'bg-amber-100 text-amber-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {vinPricing.confidence} confidence
                          </span>
                        </div>
                        <div className="flex items-end gap-6">
                          <div>
                            <p className="text-3xl font-bold text-emerald-700">${vinPricing.suggested_price?.toLocaleString()}</p>
                            <p className="text-sm text-emerald-600 mt-1">Suggested asking price</p>
                          </div>
                          <div className="text-sm text-gray-500 pb-1">
                            <p>Range: <span className="font-medium text-gray-700">${vinPricing.price_range_low?.toLocaleString()} - ${vinPricing.price_range_high?.toLocaleString()}</span></p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Vehicle History Section */}
              <div className="mt-8">
                <VehicleHistory />
              </div>
            </div>
          )}

          {/* Profit Calculator Tab */}
          {activeTab === 'profit-calc' && (
            <div className="max-w-2xl">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center shadow-lg">
                    <Calculator className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-xl">Profit Calculator</h3>
                    <p className="text-gray-500">Calculate your potential profit and ROI</p>
                  </div>
                </div>
                
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-700 font-medium">Purchase Price</Label>
                      <div className="relative mt-2">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                        <Input 
                          type="number"
                          placeholder="0"
                          value={profitCalc.purchase_price}
                          onChange={(e) => setProfitCalc({ ...profitCalc, purchase_price: e.target.value })}
                          className="pl-8 h-12 rounded-xl border-gray-200 focus:border-green-500 focus:ring-green-500"
                          data-testid="profit-purchase-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-gray-700 font-medium">Asking Price</Label>
                      <div className="relative mt-2">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                        <Input 
                          type="number"
                          placeholder="0"
                          value={profitCalc.asking_price}
                          onChange={(e) => setProfitCalc({ ...profitCalc, asking_price: e.target.value })}
                          className="pl-8 h-12 rounded-xl border-gray-200 focus:border-green-500 focus:ring-green-500"
                          data-testid="profit-asking-input"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-gray-700 font-medium">Repair Costs</Label>
                      <div className="relative mt-2">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                        <Input 
                          type="number"
                          placeholder="0"
                          value={profitCalc.repair_costs}
                          onChange={(e) => setProfitCalc({ ...profitCalc, repair_costs: e.target.value })}
                          className="pl-8 h-12 rounded-xl border-gray-200"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-gray-700 font-medium">Holding Costs</Label>
                      <div className="relative mt-2">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                        <Input 
                          type="number"
                          placeholder="0"
                          value={profitCalc.holding_costs}
                          onChange={(e) => setProfitCalc({ ...profitCalc, holding_costs: e.target.value })}
                          className="pl-8 h-12 rounded-xl border-gray-200"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-gray-700 font-medium">Selling Costs</Label>
                      <div className="relative mt-2">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                        <Input 
                          type="number"
                          placeholder="0"
                          value={profitCalc.selling_costs}
                          onChange={(e) => setProfitCalc({ ...profitCalc, selling_costs: e.target.value })}
                          className="pl-8 h-12 rounded-xl border-gray-200"
                        />
                      </div>
                    </div>
                  </div>
                  <Button 
                    onClick={handleCalculateProfit}
                    className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:opacity-90 text-white h-14 rounded-xl shadow-lg text-lg"
                    data-testid="calculate-profit-btn"
                  >
                    <Calculator className="w-5 h-5 mr-2" /> Calculate Profit
                  </Button>

                  {profitResult && (
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-100" data-testid="profit-result">
                      <div className="grid grid-cols-2 gap-6 mb-6">
                        <div className="text-center bg-white rounded-xl p-6 shadow-sm">
                          <span className="text-sm text-gray-500">Net Profit</span>
                          <p className={`text-4xl font-bold mt-2 ${profitResult.net_profit >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                            ${profitResult.net_profit?.toLocaleString()}
                          </p>
                        </div>
                        <div className="text-center bg-white rounded-xl p-6 shadow-sm">
                          <span className="text-sm text-gray-500">ROI</span>
                          <p className={`text-4xl font-bold mt-2 ${profitResult.roi >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                            {profitResult.roi}%
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div className="bg-white rounded-xl p-4">
                          <span className="text-xs text-gray-500">Gross Profit</span>
                          <p className="font-bold text-gray-900 mt-1">${profitResult.gross_profit?.toLocaleString()}</p>
                        </div>
                        <div className="bg-white rounded-xl p-4">
                          <span className="text-xs text-gray-500">Total Costs</span>
                          <p className="font-bold text-gray-900 mt-1">${profitResult.total_costs?.toLocaleString()}</p>
                        </div>
                        <div className="bg-white rounded-xl p-4">
                          <span className="text-xs text-gray-500">Margin</span>
                          <p className="font-bold text-gray-900 mt-1">{profitResult.margin}%</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* AI Tools Tab */}
          {activeTab === 'ai-tools' && (
            <div className="max-w-2xl">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-r from-violet-500 to-purple-500 flex items-center justify-center shadow-lg animate-pulse">
                    <Sparkles className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-xl">AI Description Generator</h3>
                    <p className="text-gray-500">Generate professional vehicle listings with AI</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-700 font-medium">Make</Label>
                      <Input 
                        placeholder="e.g., Toyota"
                        value={aiForm.make}
                        onChange={(e) => setAiForm({ ...aiForm, make: e.target.value })}
                        className="mt-2 h-12 rounded-xl border-gray-200 focus:border-purple-500 focus:ring-purple-500"
                        data-testid="ai-make-input"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-700 font-medium">Model</Label>
                      <Input 
                        placeholder="e.g., Camry"
                        value={aiForm.model}
                        onChange={(e) => setAiForm({ ...aiForm, model: e.target.value })}
                        className="mt-2 h-12 rounded-xl border-gray-200 focus:border-purple-500 focus:ring-purple-500"
                        data-testid="ai-model-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-gray-700 font-medium">Year</Label>
                      <Input 
                        type="number"
                        placeholder="2024"
                        value={aiForm.year}
                        onChange={(e) => setAiForm({ ...aiForm, year: e.target.value })}
                        className="mt-2 h-12 rounded-xl border-gray-200"
                        data-testid="ai-year-input"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-700 font-medium">Mileage</Label>
                      <Input 
                        type="number"
                        placeholder="50000"
                        value={aiForm.mileage}
                        onChange={(e) => setAiForm({ ...aiForm, mileage: e.target.value })}
                        className="mt-2 h-12 rounded-xl border-gray-200"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-700 font-medium">Condition</Label>
                      <Select 
                        value={aiForm.condition} 
                        onValueChange={(value) => setAiForm({ ...aiForm, condition: value })}
                      >
                        <SelectTrigger className="mt-2 h-12 rounded-xl border-gray-200">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="excellent">Excellent</SelectItem>
                          <SelectItem value="good">Good</SelectItem>
                          <SelectItem value="fair">Fair</SelectItem>
                          <SelectItem value="poor">Poor</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button 
                    onClick={handleGenerateDescription}
                    disabled={aiLoading || !aiForm.make || !aiForm.model || !aiForm.year}
                    className="w-full bg-gradient-to-r from-violet-500 to-purple-500 hover:opacity-90 text-white h-14 rounded-xl shadow-lg text-lg disabled:opacity-50"
                    data-testid="generate-description-btn"
                  >
                    <Sparkles className="w-5 h-5 mr-2" />
                    {aiLoading ? 'Generating...' : 'Generate Description'}
                  </Button>

                  {aiDescription && (
                    <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl p-6 border border-purple-100" data-testid="ai-description-result">
                      <Label className="text-gray-900 font-bold mb-3 block flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                        Generated Description
                      </Label>
                      <p className="text-gray-700 whitespace-pre-wrap leading-relaxed bg-white rounded-xl p-4">{aiDescription}</p>
                      <Button 
                        variant="outline"
                        className="mt-4 rounded-xl border-purple-200 text-purple-600 hover:bg-purple-50"
                        onClick={() => {
                          navigator.clipboard.writeText(aiDescription);
                          toast.success('Copied to clipboard!');
                        }}
                      >
                        Copy to Clipboard
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Social Media Listing Generator */}
              <div className="mt-8">
                <SocialListingGenerator />
              </div>
            </div>
          )}

          {/* CRM Tab */}
          {activeTab === 'crm' && (
            <DealerCRM />
          )}

          {/* Wholesale Marketplace Tab */}
          {activeTab === 'wholesale' && (
            <WholesaleMarketplace />
          )}

          {/* Advanced Analytics Tab */}
          {activeTab === 'analytics' && (
            <AdvancedAnalytics />
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <DocumentCenter />
          )}

          {/* Lead Network Tab */}
          {activeTab === 'lead-network' && (
            <LeadNetwork />
          )}

          {/* Sales/Invoices Tab */}
          {activeTab === 'invoices' && (
            <InvoiceManager />
          )}

          {/* Expenses Tab */}
          {activeTab === 'expenses' && (
            <ExpenseTracker />
          )}

          {/* Repairs Tab */}
          {activeTab === 'repairs' && (
            <RepairLog />
          )}

          {/* Dealer Image Pro Tab */}
          {activeTab === 'image-pro' && (
            <DealerImagePro />
          )}

          {/* Import/Export Tab */}
          {activeTab === 'import-export' && (
            <InventorySync />
          )}


          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-8">
              <ProfileSettings />
              <IdentityVerification />
              <NotificationSettings userRole={user?.role || 'dealer'} />
              <LocationManager />
            </div>
          )}
        </div>
      </main>

      {/* Add/Edit Vehicle Modal */}
      {showBulkVIN && (
        <BulkVINScanner 
          onClose={() => setShowBulkVIN(false)}
          onImportComplete={() => fetchVehicles()}
        />
      )}
      <Dialog open={showAddVehicle} onOpenChange={setShowAddVehicle}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              {editingVehicle ? 'Edit Vehicle' : 'Add New Vehicle'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddVehicle} className="space-y-5 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-700 font-medium">Stock # </Label>
                <Input 
                  value={vehicleForm.stock_number}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, stock_number: e.target.value })}
                  placeholder="e.g. WAS0326012"
                  className="mt-2 h-12 rounded-xl font-mono"
                  data-testid="vehicle-stock-input"
                />
              </div>
              <div>
                <Label className="text-gray-700 font-medium">VIN {vinAutoLoading && <span className="text-xs text-blue-500 ml-1">Decoding...</span>}</Label>
                <Input 
                  value={vehicleForm.vin}
                  onChange={(e) => {
                    const v = e.target.value.toUpperCase();
                    setVehicleForm({ ...vehicleForm, vin: v });
                    if (v.length === 17) handleVinAutoFill(v);
                  }}
                  maxLength={17}
                  placeholder="Enter 17-char VIN to auto-fill"
                  className="mt-2 h-12 rounded-xl font-mono"
                  data-testid="vehicle-vin-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-700 font-medium">Make *</Label>
                <Input 
                  value={vehicleForm.make}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, make: e.target.value })}
                  required
                  className="mt-2 h-12 rounded-xl"
                  data-testid="vehicle-make-input"
                />
              </div>
              <div>
                <Label className="text-gray-700 font-medium">Model *</Label>
                <Input 
                  value={vehicleForm.model}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, model: e.target.value })}
                  required
                  className="mt-2 h-12 rounded-xl"
                  data-testid="vehicle-model-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-gray-700 font-medium">Year *</Label>
                <Input 
                  type="number"
                  value={vehicleForm.year}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, year: e.target.value })}
                  required
                  className="mt-2 h-12 rounded-xl"
                  data-testid="vehicle-year-input"
                />
              </div>
              <div>
                <Label className="text-gray-700 font-medium">Price *</Label>
                <Input 
                  type="number"
                  value={vehicleForm.price}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, price: e.target.value })}
                  required
                  className="mt-2 h-12 rounded-xl"
                  data-testid="vehicle-price-input"
                />
              </div>
              <div>
                <Label className="text-gray-700 font-medium">Purchase Price</Label>
                <Input 
                  type="number"
                  value={vehicleForm.purchase_price}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, purchase_price: e.target.value })}
                  className="mt-2 h-12 rounded-xl"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-gray-700 font-medium">Mileage</Label>
                <Input 
                  type="number"
                  value={vehicleForm.mileage}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, mileage: e.target.value })}
                  className="mt-2 h-12 rounded-xl"
                />
              </div>
              <div>
                <Label className="text-gray-700 font-medium">Color</Label>
                <Input 
                  value={vehicleForm.color}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, color: e.target.value })}
                  placeholder="e.g. Silver"
                  className="mt-2 h-12 rounded-xl"
                  data-testid="vehicle-color-input"
                />
              </div>
              <div>
                <Label className="text-gray-700 font-medium">Title Status</Label>
                <Select 
                  value={vehicleForm.title_status} 
                  onValueChange={(value) => setVehicleForm({ ...vehicleForm, title_status: value })}
                >
                  <SelectTrigger className="mt-2 h-12 rounded-xl" data-testid="vehicle-title-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="clean">Clean</SelectItem>
                    <SelectItem value="rebuilt">Rebuilt</SelectItem>
                    <SelectItem value="salvage">Salvage</SelectItem>
                    <SelectItem value="exempt">Exempt</SelectItem>
                    <SelectItem value="court_order">Court Order</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-700 font-medium">Auction Source</Label>
                <Input 
                  value={vehicleForm.auction_source}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, auction_source: e.target.value })}
                  placeholder="e.g. Adessa, Manheim"
                  className="mt-2 h-12 rounded-xl"
                  data-testid="vehicle-auction-input"
                />
              </div>
              <div>
                <Label className="text-gray-700 font-medium">Lot Location</Label>
                <Input 
                  value={vehicleForm.lot_location}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, lot_location: e.target.value })}
                  placeholder="Lot / ZIP"
                  className="mt-2 h-12 rounded-xl"
                  data-testid="vehicle-lot-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-700 font-medium">Condition</Label>
                <Select 
                  value={vehicleForm.condition} 
                  onValueChange={(value) => setVehicleForm({ ...vehicleForm, condition: value })}
                >
                  <SelectTrigger className="mt-2 h-12 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="excellent">Excellent</SelectItem>
                    <SelectItem value="good">Good</SelectItem>
                    <SelectItem value="fair">Fair</SelectItem>
                    <SelectItem value="poor">Poor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-700 font-medium">Status</Label>
                <Select 
                  value={vehicleForm.status} 
                  onValueChange={(value) => setVehicleForm({ ...vehicleForm, status: value })}
                >
                  <SelectTrigger className="mt-2 h-12 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="sold">Sold</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-gray-700 font-medium">Description</Label>
              <Textarea 
                value={vehicleForm.description}
                onChange={(e) => setVehicleForm({ ...vehicleForm, description: e.target.value })}
                className="mt-2 rounded-xl"
                rows={2}
              />
            </div>
            <div>
              <Label className="text-gray-700 font-medium">Remarks</Label>
              <Input 
                value={vehicleForm.remarks}
                onChange={(e) => setVehicleForm({ ...vehicleForm, remarks: e.target.value })}
                placeholder="e.g. not registered yet, TOP"
                className="mt-2 h-12 rounded-xl"
                data-testid="vehicle-remarks-input"
              />
            </div>
            <div>
              <Label className="text-gray-700 font-medium">Photos</Label>
              <div className="mt-2">
                <ImageUploader
                  images={vehicleForm.images}
                  onImagesChange={(imgs) => setVehicleForm({ ...vehicleForm, images: imgs })}
                  maxImages={10}
                />
              </div>
            </div>
            <div className="flex justify-end gap-4 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                className="rounded-xl"
                onClick={() => {
                  setShowAddVehicle(false);
                  setEditingVehicle(null);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90 text-white rounded-xl shadow-lg" data-testid="save-vehicle-btn">
                {editingVehicle ? 'Update Vehicle' : 'Add Vehicle'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
